import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react';
import React from 'react';
import * as Recharts from 'recharts';
import styled from 'styled-components';
import { TYPES as FluoroTYPES } from './types';
import { TYPES } from '@/engine/ioc';
import type { IFluoroCurveController, IFluoroCurveControllerFactory } from './interfaces';
import type { CategoricalChartState } from 'recharts/types/chart/types';
import { useTheme } from '@mui/material/styles';
import ErrorIcon from '@mui/icons-material/Error';
import type { ViewsService } from '@/engine/app/Views.service';

export const FluoroCurve: React.FC = observer(() => {
  const theme = useTheme();
  const [controller, setController] = React.useState<IFluoroCurveController | undefined>(undefined);
  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);

  const viewsService = useInjection<ViewsService>(TYPES.ViewsService);

  const currentView = viewsService.currentView;
  if (!currentView) {
    return <ErrorIcon />;
  }

  const factory = useInjection<IFluoroCurveControllerFactory>(FluoroTYPES.IFluoroCurveControllerFactory);

  React.useEffect(() => {
    const contr = factory.createController(currentView);
    setController(contr);
    return (): void => {
      contr.dispose();
    };
  }, [currentView, factory]);

  const _formatter = (value: string): [string, string] => {
    const val = Number(value);
    return ['', (val < 0 ? 'CAUD' : 'CRAN') + ': ' + Math.abs(val).toFixed() + '° '];
  };

  const _labelFormatter = (value: string): string => {
    const val = Number(value);
    return (val < 0 ? 'RAO' : 'LAO') + ': ' + Math.abs(val).toFixed() + '° ';
  };

  const _validateState = (state?: CategoricalChartState): boolean =>
    state?.activeLabel !== undefined && state?.activePayload !== undefined && state.activePayload.length > 0;

  const _onMouseUp = (): void => {
    if (isProcessing) {
      setIsProcessing(false);
    }
  };

  const _onMouseDown = (state?: CategoricalChartState): void => {
    if (controller !== undefined && _validateState(state)) {
      setIsProcessing(true);
      const raoLao = Number(state?.activeLabel);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const caudCran = state?.activePayload?.[0].value as number;
      controller.setCamera(raoLao, caudCran);
    }
  };

  const _onMouseMove = (state?: CategoricalChartState): void => {
    if (isProcessing && controller !== undefined && _validateState(state)) {
      const raoLao = Number(state?.activeLabel);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const caudCran = state?.activePayload?.[0].value as number;
      controller.setCamera(raoLao, caudCran);
    }
  };

  const rechartsColor =
    theme.mode === 'dark'
      ? {
        fill: theme.font
      }
      : {};
  return (
    <>
      {' '}
      {!controller?.isActive() ? null : (
        <Container data-testid="fluoroCurve">
          <Recharts.ResponsiveContainer width="100%" height="100%">
            <Recharts.LineChart
              width={500}
              height={300}
              data={controller.data}
              margin={{ top: 25, right: 50, left: 50, bottom: 25 }}
              onMouseDown={_onMouseDown}
              onMouseMove={_onMouseMove}
              onMouseUp={_onMouseUp}
              onMouseLeave={_onMouseUp}
            >
              <Recharts.XAxis hide={true} dataKey="raoLao" type="number" />
              <Recharts.YAxis hide={true} dataKey="caudCran" type="number" />
              <Recharts.Tooltip
                position={{ x: 0, y: 0 }}
                cursor={false}
                separator=""
                formatter={_formatter}
                labelFormatter={_labelFormatter}
                itemStyle={{ color: '#666666' }}
                labelStyle={{ color: '#666666' }}
              />
              <Recharts.ReferenceLine y={0} stroke={theme.palette.secondary.main}>
                <Recharts.Label value="LAO" offset={10} position="right" {...rechartsColor} />
                <Recharts.Label value="RAO" offset={10} position="left" {...rechartsColor} />
              </Recharts.ReferenceLine>
              <Recharts.ReferenceLine x={0} stroke={theme.palette.secondary.main}>
                <Recharts.Label value="CRAN" offset={10} position="top" {...rechartsColor} />
                <Recharts.Label value="CAUD" offset={10} position="bottom" {...rechartsColor} />
              </Recharts.ReferenceLine>
              <Recharts.Line
                isAnimationActive={false}
                dot={{ strokeWidth: 1 }}
                type="monotone"
                dataKey="caudCran"
                stroke={theme.palette.primary.main}
                strokeWidth={3}
                activeDot={{ r: 6 }}
              />
              <Recharts.ReferenceDot
                x={controller.current.raoLao}
                y={controller.current.caudCran}
                r={4}
                fill={controller.current.isInverted ? theme.palette.error.main : theme.customPalette.objects.selected}
                stroke="none"
              />
            </Recharts.LineChart>
          </Recharts.ResponsiveContainer>
        </Container>
      )}{' '}
    </>
  );
});

const Container = styled.div`
  width: 100%;
  height: 10em;
  margin-top: 2em;
  user-select: none;
`;
